/**
 * BroadcastPanel — рассылка клиентам из «Аудитории» и «Активности» (06.10.2026).
 * Владелец: «вчера пытались сделать рассылку с Юнифанса, ничего не получилось». 05.10 сообщение
 * дошло до ~404 клиентов, но экран две минуты молчал и показал итог на 2,5 с, а вторая попытка
 * прислала повторы. Теперь: черновик (с датой, автором и «уже доставлено»), предпросмотр «кому
 * уйдёт / кого отсеем», «Тест себе», запуск с подтверждением, ход и отчёт в панели — до закрытия.
 * Закрыть можно в любой момент: рассылка идёт на сервере и дойдёт сама.
 */
import { useEffect, useState } from "react";
import { useBroadcast, type BroadcastJob, type Draft } from "./useBroadcast";
import css from "./BroadcastPanel.module.css";

/** «2026-10-05 11:41:32» (UTC) → «05.10 16:41» по Оренбургу. */
function local(ts: string): string {
  const d = new Date(`${ts.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return "";
  const o = new Date(d.getTime() + 5 * 3600_000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(o.getUTCDate())}.${p(o.getUTCMonth() + 1)} ${p(o.getUTCHours())}:${p(o.getUTCMinutes())}`;
}

/** С этой даты (UTC) у каждой рассылки есть журнал получателей; раньше — нет. */
const LOG_SINCE = "2026-10-06";

const ROWS: { key: string; label: string; cls?: "good" | "bad" | "muted" }[] = [
  { key: "sent", label: "Доставлено", cls: "good" },
  { key: "never_started", label: "Не нажимали «Старт» в боте", cls: "muted" },
  { key: "blocked", label: "Заблокировали бота", cls: "muted" },
  { key: "deactivated", label: "Удалили аккаунт", cls: "muted" },
  { key: "unknown", label: "Нет ответа (могло дойти)" },
  { key: "failed", label: "Ошибки", cls: "bad" },
  { key: "cancelled", label: "Отменено", cls: "muted" },
];

function JobView({ job, busy, onCancel, onNew, onContinue }: { job: BroadcastJob; busy: boolean; onCancel: () => void; onNew: () => void; onContinue: () => void }) {
  const [sure, setSure] = useState(false);
  const c = job.counts;
  const queue = (c.pending ?? 0) + (c.sending ?? 0);
  const done = Math.max(0, job.total - queue);
  const pct = job.total ? Math.round((done / job.total) * 100) : 100;
  const wait = Math.ceil((job.next_run_at - Date.now()) / 1000);
  const head = job.status === "running" ? "Идёт рассылка" : job.status === "done" ? "Рассылка завершена"
    : job.status === "cancelled" ? "Рассылка остановлена" : "Рассылка не удалась";
  return (
    <>
      <div className={css.title}>{head}</div>
      <div className={css.hint}>«{job.preview}» · {job.created_by_name ?? "сотрудник"} · {local(job.created_at)}</div>
      {job.error ? <div className={css.err}>{job.error}</div> : null}
      <div className={css.bar}><div className={css.fill} style={{ width: `${pct}%` }} /></div>
      <div className={css.hint}>Обработано {done} из {job.total}{job.skipped ? ` · отсеяно до отправки ${job.skipped}` : ""}</div>
      <div className={css.sum}>
        {ROWS.filter((r) => (c[r.key] ?? 0) > 0 || r.key === "sent").map((r) => (
          <FragmentRow key={r.key} label={r.label} n={c[r.key] ?? 0} cls={r.cls} />
        ))}
        {queue ? <FragmentRow label="В очереди" n={queue} /> : null}
      </div>
      {job.status === "running" && wait > 0 ? <div className={css.hint}>Пауза {wait} с: Telegram просит подождать или не отвечает — продолжим сами.</div> : null}
      {job.status === "running" ? <div className={css.hint}>Можно закрыть приложение — рассылка дойдёт сама.</div> : null}
      {job.errors.length ? <div className={css.err}>{job.errors.slice(0, 3).map((e) => `${e.error} × ${e.n}`).join(" · ")}</div> : null}
      {job.status === "running" ? (
        sure ? (
          <div className={css.confirm}>
            <b>Остановить? Кому ещё не ушло — не уйдёт.</b>
            <div className={css.row}>
              <button type="button" className={`${css.btn} ${css.danger}`} disabled={busy} onClick={onCancel}>Да, остановить</button>
              <button type="button" className={css.btn} onClick={() => setSure(false)}>Нет</button>
            </div>
          </div>
        ) : <button type="button" className={`${css.btn} ${css.danger}`} onClick={() => setSure(true)}>Остановить рассылку</button>
      ) : (
        <>
          {job.status === "failed" && /исходное сообщение удалено/.test(job.error) ? (
            <button type="button" className={css.btn} onClick={onContinue}>Дослать остальным новым черновиком</button>
          ) : null}
          <button type="button" className={css.main} onClick={onNew}>Новая рассылка</button>
        </>
      )}
    </>
  );
}

function FragmentRow({ label, n, cls }: { label: string; n: number; cls?: "good" | "bad" | "muted" }) {
  return (<><span className={cls ? css[cls] : undefined}>{label}</span><b className={cls ? css[cls] : undefined}>{n}</b></>);
}

function DraftRow({ d, on, onPick }: { d: Draft; on: boolean; onPick: () => void }) {
  return (
    <label className={`${css.draft} ${on ? css.on : ""}`}>
      <input type="radio" name="bcast-draft" checked={on} onChange={onPick} />
      <span className={css.dText}>
        {d.preview}
        <span className={css.dMeta}>
          {local(d.created_at)}{d.author ? ` · ${d.author}` : ""}
          {d.sent ? <span className={css.dSent}> · уже доставлено {d.sent}</span> : null}
          {/* до новой рассылки журнала не было: 05.10 «Осенние каникулы» уже дошли до ~404 человек */}
          {!d.sent && d.created_at < LOG_SINCE ? <span className={css.dSent}> · до 06.10 журнала нет — мог уже рассылаться</span> : null}
        </span>
      </span>
    </label>
  );
}

export interface BroadcastPanelProps {
  selectedIds: number[];
  title?: string;
  onClose: () => void;
  /** Закрыли после запуска рассылки — хост снимает выделение. */
  onSent?: () => void;
  onToast: (m: string) => void;
}

export function BroadcastPanel({ selectedIds, title = "Рассылка", onClose, onSent, onToast }: BroadcastPanelProps) {
  const b = useBroadcast(selectedIds);
  const [sure, setSure] = useState(false);
  const [started, setStarted] = useState(false);
  const close = () => (started && onSent ? onSent() : onClose());
  useEffect(() => { setSure(false); }, [b.draftId, selectedIds.length]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const p = b.preview;
  const eligible = p?.eligible ?? 0;

  const test = async () => {
    const j = await b.start("test");
    if (!j) return;
    onToast("Тест отправляется…");
    const r = await b.testOutcome(j);
    onToast((r.counts.sent ?? 0) > 0 ? "Тест доставлен вам в личку от бота — проверьте, как выглядит сообщение"
      : (r.counts.never_started ?? 0) + (r.counts.blocked ?? 0) > 0 ? `Бот не может вам написать — откройте @${b.bot || "бота"} и нажмите «Старт»`
      : r.status === "running" ? "Тест ещё в очереди — придёт в течение минуты" : `Тест не дошёл: ${r.errors[0]?.error ?? r.error ?? "причина неизвестна"}`);
  };
  const go = async () => {
    setSure(false);
    if (await b.start("normal")) setStarted(true);
  };

  return (
    <div className={css.back} onClick={close}>
      <div className={css.sheet} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className={css.head}>
          <span className={css.title}>{title} · выбрано {selectedIds.length}</span>
          <button type="button" className={css.x} aria-label="Закрыть" onClick={close}>✕</button>
        </div>
        {b.error ? <div className={css.err}>{b.error}</div> : null}
        {b.job ? (
          <JobView job={b.job} busy={b.busy} onCancel={() => void b.cancel()} onNew={() => b.reset()} onContinue={() => b.reset(b.job)} />
        ) : (
          <>
            {b.other ? (
              <div className={css.sum}>
                <span>Идёт рассылка «{b.other.preview.slice(0, 40)}» · {b.other.created_by_name ?? "сотрудник"}</span>
                <button type="button" className={css.btn} onClick={() => b.other && b.watch(b.other)}>Посмотреть</button>
              </div>
            ) : null}
            {b.continueOf ? <div className={css.hint}>Досылка после прерванной рассылки: кто получил прежний черновик, повтор не получит.</div> : null}
            <p className={css.hint}>
              Пришлите сообщение (текст, фото, видео, голос, опрос) в личку боту{" "}
              {b.bot ? <a href={`https://t.me/${b.bot}`} target="_blank" rel="noreferrer">@{b.bot}</a> : <b>рассылки</b>}
              {" "}— оно станет черновиком. Потом выберите его здесь.
            </p>
            <div className={css.row}>
              <button type="button" className={css.btn} onClick={b.loadDrafts} disabled={b.loading}>{b.loading ? "Загружаю…" : "Обновить черновики"}</button>
            </div>
            {!b.loading && !b.drafts.length ? <p className={css.hint}>Черновиков нет.</p> : null}
            <div className={css.drafts}>
              {b.drafts.map((d) => <DraftRow key={d.id} d={d} on={b.draftId === d.id} onPick={() => b.setDraftId(d.id)} />)}
            </div>
            {p ? (
              <div className={css.sum}>
                <span>Получат</span><b className={css.good}>{p.eligible}</b>
                {p.already ? <><span className={css.muted}>Уже получали этот черновик</span><b className={css.muted}>{p.already}</b></> : null}
                {p.unreachable ? <><span className={css.muted}>Бот не может написать (нет «Старт» или блок)</span><b className={css.muted}>{p.unreachable}</b></> : null}
                {p.staff ? <><span className={css.muted}>Сотрудники</span><b className={css.muted}>{p.staff}</b></> : null}
                {p.excluded ? <><span className={css.muted}>Исключены и конкуренты</span><b className={css.muted}>{p.excluded}</b></> : null}
                {p.optout ? <><span className={css.muted}>Отписались от бота</span><b className={css.muted}>{p.optout}</b></> : null}
              </div>
            ) : null}
            <div className={css.row}>
              <button type="button" className={css.btn} disabled={!b.draftId || b.busy} onClick={() => void test()}>Тест себе</button>
            </div>
            {sure ? (
              <div className={css.confirm}>
                <b>Отправить {eligible} получателям? Каждый получит сообщение один раз.</b>
                <div className={css.row}>
                  <button type="button" className={`${css.main} ${css.warn}`} disabled={b.busy} onClick={() => void go()}>{b.busy ? "Запускаю…" : "Да, разослать"}</button>
                  <button type="button" className={css.btn} onClick={() => setSure(false)}>Отмена</button>
                </div>
              </div>
            ) : (
              <button type="button" className={css.main} disabled={!b.draftId || !eligible || b.busy} onClick={() => setSure(true)}>
                {!selectedIds.length ? "Сначала выделите получателей" : !b.draftId ? "Выберите черновик" : p && !eligible ? "Некому отправлять" : `Разослать ${eligible}`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
